/*
  GROCERY wishlist (heart A + sign-in option 1, Oct 2026).

  - Hearts ([data-gro-wish], snippets/gro-wishlist-heart.liquid) save and remove
    through the wishlist app's proxy, POST /apps/wishlist/toggle. The list lives
    on the customer record (metafield gro.wishlist), so it follows them to every
    device. Server: Development/wishlist/server.
  - Signed out, a heart opens <gro-wishlist-signin>. The tapped piece waits in
    sessionStorage and is saved on the first page load after they sign in or
    join, whichever page Shopify lands them on.
  - Announces `gro:wishlist` on document; assets/gro-analytics.js sends it to GA4.
  - On the wishlist page: size picker + add to cart without leaving the page, and the
    GRID / BOARD switch (the mood board, assets/gro-board.js, loads on first use).
  - cfg.api: save / remove / sign-in / add to cart for the board.
*/
(() => {
  const cfg = window.GroWishlist;
  if (!cfg || window.GroWishlistReady) return;
  window.GroWishlistReady = true;

  const PENDING_KEY = 'gro:wish-pending';
  const PENDING_TTL = 30 * 60 * 1000;
  // Prices come in the shopper's currency (Markets: a visitor abroad sees US$, €…), so the
  // label follows Shopify.currency.active rather than assuming HK$ (team, 13 Oct).
  const CURRENCY = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'HKD';
  const money = new Intl.NumberFormat('en-HK', { style: 'currency', currency: CURRENCY, minimumFractionDigits: 2, maximumFractionDigits: 2 });
  let saved = new Set((cfg.ids || []).map(String));
  const busy = new Set();

  const pending = {
    get() {
      try {
        return JSON.parse(sessionStorage.getItem(PENDING_KEY) || 'null');
      } catch (e) {
        return null;
      }
    },
    set(value) {
      try {
        sessionStorage.setItem(PENDING_KEY, JSON.stringify(value));
      } catch (e) {}
    },
    clear() {
      try {
        sessionStorage.removeItem(PENDING_KEY);
      } catch (e) {}
    },
  };

  /* ---------- state on the page ---------- */

  const label = (isSaved, title) => (isSaved ? cfg.strings.remove : cfg.strings.save).replace('%TITLE%', title || '');

  function paint() {
    document.querySelectorAll('[data-gro-wish]').forEach((btn) => {
      const isSaved = saved.has(btn.dataset.groWish);
      if (btn.getAttribute('aria-pressed') !== String(isSaved)) btn.setAttribute('aria-pressed', String(isSaved));
      btn.setAttribute('aria-label', label(isSaved, btn.dataset.groWishTitle));
    });
    document.querySelectorAll('[data-gro-wish-count]').forEach((el) => {
      el.textContent = saved.size;
      el.hidden = saved.size === 0;
    });
    document.querySelectorAll('[data-gro-wl-count]').forEach((el) => {
      el.textContent = saved.size;
    });
  }

  const infoFrom = (btn) => ({
    id: btn.dataset.groWish,
    title: btn.dataset.groWishTitle || '',
    price: parseFloat(btn.dataset.groWishPrice) || undefined,
    image: btn.dataset.groWishImage || '',
  });

  function announce(action, info, count, source) {
    document.dispatchEvent(
      new CustomEvent('gro:wishlist', {
        detail: { action, id: String(info.id), title: info.title || '', price: info.price, count, source },
      })
    );
  }

  /* ---------- the "Saved" note ---------- */

  const toastEl = document.querySelector('[data-gro-wish-toast]');
  let toastTimer;

  function toast(message, link) {
    if (!toastEl) return;
    toastEl.querySelector('[data-gro-wish-toast-text]').textContent = message;
    const a = toastEl.querySelector('[data-gro-wish-toast-link]');
    a.hidden = !link;
    if (link) {
      a.textContent = link.text;
      a.href = link.href;
    }
    // Sit just under the header when it's on screen, at the top when it has scrolled away.
    const header = document.querySelector('.section-header');
    const top = header ? Math.max(0, header.getBoundingClientRect().bottom) : 0;
    toastEl.style.setProperty('--gro-wish-toast-top', `${Math.round(top)}px`);
    toastEl.hidden = false;
    requestAnimationFrame(() => toastEl.classList.add('is-on'));
    clearTimeout(toastTimer);
    toastTimer = setTimeout(hideToast, 4000);
  }

  function hideToast() {
    toastEl.classList.remove('is-on');
    setTimeout(() => {
      if (!toastEl.classList.contains('is-on')) toastEl.hidden = true;
    }, 260);
  }

  const savedLink = () => ({ text: cfg.strings.view, href: cfg.pageUrl });

  /* ---------- saving ---------- */

  async function request(id, action) {
    const res = await fetch(`${cfg.proxy}/toggle`, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ productId: id, action }),
    });
    const json = await res.json().catch(() => ({}));
    if (!res.ok || !json.ok) throw new Error(json.error || String(res.status));
    return json;
  }

  function syncFrom(json) {
    saved = new Set(json.ids.map(String));
    paint();
  }

  async function toggle(btn) {
    const info = infoFrom(btn);
    if (busy.has(info.id)) return;
    const action = saved.has(info.id) ? 'remove' : 'add';
    busy.add(info.id);
    if (action === 'add') saved.add(info.id);
    else saved.delete(info.id);
    paint();
    try {
      const json = await request(info.id, action);
      syncFrom(json);
      announce(action, info, json.count, 'heart');
      if (action === 'add') toast(cfg.strings.saved, savedLink());
      else leave(info.id);
    } catch (e) {
      if (action === 'add') saved.delete(info.id);
      else saved.add(info.id);
      paint();
      toast(cfg.strings.error);
    } finally {
      busy.delete(info.id);
    }
  }

  // On the wishlist page a removed piece leaves the grid.
  function leave(id) {
    document.querySelectorAll(`[data-gro-wish-item="${id}"]`).forEach((el) => {
      el.classList.add('is-leaving');
      setTimeout(() => {
        el.remove();
        const empty = document.querySelector('[data-gro-wl-empty]');
        if (empty && !document.querySelector('[data-gro-wish-item]')) empty.hidden = false;
      }, 260);
    });
  }

  /* ---------- sign-in panel ---------- */

  class GroWishlistSignin extends HTMLElement {
    connectedCallback() {
      this.panel = this.querySelector('.gro-ws__panel');
      this.addEventListener('click', (e) => {
        if (e.target.closest('[data-gro-ws-close]')) {
          e.preventDefault();
          this.close(true);
          return;
        }
        const tab = e.target.closest('[data-gro-ws-tab]');
        if (tab) this.mode(tab.dataset.groWsTab, true);
      });
      this.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') {
          e.preventDefault();
          e.stopPropagation();
          this.close(true);
        } else if (e.key === 'Tab') {
          e.stopPropagation();
          this.trap(e);
        } else if ((e.key === 'ArrowRight' || e.key === 'ArrowLeft') && e.target.matches('[data-gro-ws-tab]')) {
          this.mode(e.target.dataset.groWsTab === 'login' ? 'join' : 'login', true);
        }
      });
    }

    open(info, mode, opener) {
      // Safari doesn't focus a tapped button, so the opener is passed in, not read from focus.
      this.opener = opener || document.activeElement;
      const back = location.pathname + location.search;
      this.querySelectorAll('[data-gro-ws-return]').forEach((input) => {
        input.value = back;
      });

      const row = this.querySelector('[data-gro-ws-product]');
      row.hidden = !(info && info.title);
      if (info && info.title) {
        row.querySelector('[data-gro-ws-name]').textContent = info.title;
        row.querySelector('[data-gro-ws-price]').textContent = info.price ? money.format(info.price) : '';
        const img = row.querySelector('[data-gro-ws-image]');
        img.hidden = !info.image;
        if (info.image) img.src = info.image;
      }

      this.mode(mode || 'login', false);
      this.hidden = false;
      document.body.classList.add('gro-ws-open');
      // Focus straight away; only the fade waits for the next frame.
      const first = [...this.querySelectorAll('[role="tabpanel"]')]
        .find((p) => !p.hidden)
        ?.querySelector('input:not([type="hidden"])');
      (first || this.panel).focus({ preventScroll: true });
      requestAnimationFrame(() => this.classList.add('is-open'));
    }

    close(cancelled) {
      if (this.hidden) return;
      if (cancelled) pending.clear();
      this.classList.remove('is-open');
      document.body.classList.remove('gro-ws-open');
      setTimeout(() => {
        if (!this.classList.contains('is-open')) this.hidden = true;
      }, 240);
      if (this.opener && this.opener.focus) this.opener.focus({ preventScroll: true });
    }

    mode(name, focusTab) {
      this.querySelectorAll('[data-gro-ws-mode]').forEach((el) => {
        el.hidden = el.dataset.groWsMode !== name;
      });
      this.querySelectorAll('[data-gro-ws-tab]').forEach((tab) => {
        const on = tab.dataset.groWsTab === name;
        tab.setAttribute('aria-selected', String(on));
        tab.tabIndex = on ? 0 : -1;
        if (on && focusTab) tab.focus();
      });
    }

    trap(e) {
      const items = [
        ...this.panel.querySelectorAll('a[href], button, input:not([type="hidden"]), [tabindex]:not([tabindex="-1"])'),
      ].filter((el) => !el.disabled && !el.closest('[hidden]') && el.tabIndex !== -1);
      if (!items.length) return;
      const first = items[0];
      const last = items[items.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }
  }
  if (!customElements.get('gro-wishlist-signin')) customElements.define('gro-wishlist-signin', GroWishlistSignin);

  function openSignin(info, mode, opener) {
    const panel = document.querySelector('gro-wishlist-signin');
    // A shared board's "Save all" waits with a list of ids instead of one.
    if (info) pending.set({ id: info.id, ids: info.ids, title: info.title, price: info.price, at: Date.now() });
    if (!panel || typeof panel.open !== 'function') {
      window.location.href = '/account/login';
      return;
    }
    panel.open(info, mode, opener);
  }

  /* ---------- clicks ---------- */

  document.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-gro-wish]');
    if (btn) {
      e.preventDefault();
      if (cfg.signedIn) toggle(btn);
      else openSignin(infoFrom(btn), 'login', btn);
      return;
    }
    const opener = e.target.closest('[data-gro-ws-open]');
    if (opener && !cfg.signedIn) {
      e.preventDefault();
      openSignin(null, opener.dataset.groWsOpen || 'login', opener);
    }
  });

  /* ---------- wishlist page: size + add to cart ---------- */

  document.addEventListener('change', (e) => {
    const size = e.target.closest('[data-gro-wl-size]');
    if (!size || !size.form) return;
    size.form.querySelector('[name="id"]').value = size.value;
    const add = size.form.querySelector('[data-gro-wl-add]');
    if (add) add.disabled = false;
  });

  async function addToCart(id) {
    const routes = window.routes || {};
    try {
      const drawer = document.querySelector('cart-drawer');
      const sections = drawer ? 'cart-icon-bubble,cart-drawer' : 'cart-icon-bubble';
      const res = await fetch(`${routes.cart_add_url || '/cart/add'}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id, quantity: 1 }], sections, sections_url: location.pathname }),
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.description || json.message || cfg.strings.error);
      const bubble = json.sections && json.sections['cart-icon-bubble'];
      const target = document.getElementById('cart-icon-bubble');
      if (bubble && target) {
        const fresh = new DOMParser().parseFromString(bubble, 'text/html').querySelector('.shopify-section');
        if (fresh) target.innerHTML = fresh.innerHTML;
      }
      // The cart drawer is rendered by the server: without a refresh the bag icon opens the
      // bag as it was when the page loaded (team, 13 Oct). Same swap as Dawn's renderContents,
      // without opening the drawer over the board.
      const drawerHtml = json.sections && json.sections['cart-drawer'];
      const current = document.getElementById('CartDrawer');
      if (drawer && drawerHtml && current) {
        const fresh = new DOMParser().parseFromString(drawerHtml, 'text/html').querySelector('#CartDrawer');
        if (fresh) {
          current.innerHTML = fresh.innerHTML;
          drawer.classList.remove('is-empty');
          const inner = drawer.querySelector('.drawer__inner');
          if (inner) inner.classList.remove('is-empty');
          const overlay = drawer.querySelector('#CartDrawer-Overlay');
          if (overlay && typeof drawer.close === 'function') overlay.addEventListener('click', () => drawer.close());
        }
      }
      toast(cfg.strings.added, { text: cfg.strings.viewCart, href: routes.cart_url || '/cart' });
      return true;
    } catch (err) {
      toast(err.message || cfg.strings.error);
      return false;
    }
  }

  document.addEventListener('submit', async (e) => {
    const form = e.target.closest('[data-gro-wl-form]');
    if (!form) return;
    e.preventDefault();
    const add = form.querySelector('[data-gro-wl-add]');
    const id = Number(form.querySelector('[name="id"]').value);
    if (!id) return;
    add.disabled = true;
    add.setAttribute('aria-busy', 'true');
    await addToCart(id);
    add.disabled = false;
    add.removeAttribute('aria-busy');
  });

  /* ---------- wishlist page: GRID / BOARD ---------- */

  const VIEW_KEY = 'gro:wl-view';
  let boardLoading = null;

  function loadBoard(root) {
    if (window.GroBoard) return Promise.resolve();
    if (boardLoading) return boardLoading;
    boardLoading = new Promise((resolve, reject) => {
      const css = document.createElement('link');
      css.rel = 'stylesheet';
      css.href = root.dataset.css;
      document.head.appendChild(css);
      const js = document.createElement('script');
      js.src = root.dataset.js;
      js.onload = () => resolve();
      js.onerror = reject;
      document.head.appendChild(js);
    });
    return boardLoading;
  }

  async function showView(view, remember) {
    const root = document.querySelector('[data-gro-board]');
    const grid = document.querySelector('.gro-wl__grid');
    if (!root || !grid) return;
    document.querySelectorAll('[data-gro-wl-view]').forEach((btn) => {
      btn.setAttribute('aria-pressed', String(btn.dataset.groWlView === view));
    });
    if (remember) {
      try {
        localStorage.setItem(VIEW_KEY, view);
      } catch (e) {}
    }
    if (view === 'board') {
      try {
        await loadBoard(root);
      } catch (e) {
        toast(cfg.strings.error);
        return showView('grid', true);
      }
      grid.hidden = true;
      root.hidden = false;
      window.GroBoard.open(root, { onClose: () => showView('grid', true) });
      // Bring the whole board into view just under the sticky header: from its toolbar on
      // desktop, from the GRID / BOARD switch on phones (the way back stays on screen).
      const phone = window.matchMedia('(max-width: 749px)').matches;
      const anchor = phone ? document.querySelector('.gro-wl__views') : root.querySelector('.gro-board__bar');
      if (remember && anchor && root.getBoundingClientRect().bottom > window.innerHeight) {
        const header = document.querySelector('.section-header');
        const offset = header && getComputedStyle(header).position === 'sticky' ? header.offsetHeight : 0;
        const calm = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        window.scrollBy({ top: anchor.getBoundingClientRect().top - offset - (phone ? 0 : 8), behavior: calm ? 'auto' : 'smooth' });
      }
    } else {
      if (window.GroBoard) window.GroBoard.close(root);
      root.hidden = true;
      grid.hidden = false;
    }
  }

  document.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-gro-wl-view]');
    if (!btn) return;
    showView(btn.dataset.groWlView, true);
  });

  if (document.querySelector('[data-gro-board]')) {
    let last = null;
    try {
      last = localStorage.getItem(VIEW_KEY);
    } catch (e) {}
    if (last === 'board') showView('board', false);
  }

  /* ---------- for the mood board (assets/gro-board.js) ---------- */

  cfg.api = {
    has: (id) => saved.has(String(id)),
    async save(info, source) {
      const id = String(info.id);
      if (saved.has(id)) return true;
      saved.add(id);
      paint();
      try {
        const json = await request(id, 'add');
        syncFrom(json);
        announce('add', info, json.count, source || 'board');
        return true;
      } catch (e) {
        saved.delete(id);
        paint();
        toast(cfg.strings.error);
        return false;
      }
    },
    async remove(info, source) {
      const id = String(info.id);
      saved.delete(id);
      paint();
      try {
        const json = await request(id, 'remove');
        syncFrom(json);
        announce('remove', info, json.count, source || 'board');
        leave(id);
        return true;
      } catch (e) {
        saved.add(id);
        paint();
        toast(cfg.strings.error);
        return false;
      }
    },
    openSignin: (info, mode, opener) => openSignin(info, mode, opener),
    toast: (text, link) => toast(text, link),
    addToCart,
  };

  /* ---------- after sign-in: save the piece they tapped ---------- */

  const waiting = pending.get();
  if (waiting && cfg.signedIn && Array.isArray(waiting.ids) && waiting.ids.length) {
    pending.clear();
    if (Date.now() - (waiting.at || 0) < PENDING_TTL) {
      (async () => {
        let last = null;
        for (const raw of waiting.ids.slice(0, 60)) {
          const id = String(raw);
          if (saved.has(id)) continue;
          try {
            last = await request(id, 'add');
            announce('add', { id }, last.count, 'board_shared');
          } catch (e) {}
        }
        if (last) syncFrom(last);
        toast(cfg.strings.saved, savedLink());
      })();
    }
  } else if (waiting && cfg.signedIn) {
    pending.clear();
    const id = String(waiting.id || '');
    if (id && Date.now() - (waiting.at || 0) < PENDING_TTL) {
      if (saved.has(id)) {
        toast(cfg.strings.saved, savedLink());
      } else {
        request(id, 'add')
          .then((json) => {
            syncFrom(json);
            announce('add', waiting, json.count, 'after_sign_in');
            toast(cfg.strings.saved, savedLink());
          })
          .catch(() => toast(cfg.strings.error));
      }
    }
  }

  /* ---------- hearts that arrive later (search panel, filters, carousels) ---------- */

  paint();
  if ('MutationObserver' in window) {
    let queued = false;
    new MutationObserver((records) => {
      if (queued) return;
      const hasHeart = records.some((r) =>
        [...r.addedNodes].some((n) => n.nodeType === 1 && (n.matches('[data-gro-wish]') || n.querySelector('[data-gro-wish]')))
      );
      if (!hasHeart) return;
      queued = true;
      requestAnimationFrame(() => {
        queued = false;
        paint();
      });
    }).observe(document.body, { childList: true, subtree: true });
  }
})();
